import { NetworkStatus, gql, useMutation } from '@apollo/client';
import { useToast } from '@chakra-ui/react';
import * as React from 'react';

import { useQueryWithPastDataDeprecated } from '@/imports/ui/useQueryWithPastData';

import {
  BulkMoveLibraryFilesModal_libraryFolder,
  BulkMoveLibraryFilesModal_libraryFolderVariables,
} from './graphql-types/BulkMoveLibraryFilesModal_libraryFolder';
import {
  BulkMoveLibraryFilesModal_moveGroupLibraryFiles,
  BulkMoveLibraryFilesModal_moveGroupLibraryFilesVariables,
} from './graphql-types/BulkMoveLibraryFilesModal_moveGroupLibraryFiles';

import {
  MOVE_ITEMS_MODAL_LIBRARY_FOLDER_FRAGMENT,
  MoveItemsModal,
} from './MoveItemsModal';

const GET_LIBRARY_FOLDERS = gql`
  query BulkMoveLibraryFilesModal_libraryFolder(
    $input: SingleLibraryFolderInputV2!
  ) {
    libraryFolderV2(input: $input) {
      result {
        _id
        ...MoveItemsModal_LibraryFolderFragment
        subFolders {
          results {
            _id
            ...MoveItemsModal_LibraryFolderFragment
          }
        }
      }
    }
  }
  ${MOVE_ITEMS_MODAL_LIBRARY_FOLDER_FRAGMENT}
`;

const MOVE_LIBRARY_FILES_AST = gql`
  mutation BulkMoveLibraryFilesModal_moveGroupLibraryFiles(
    $input: MoveGroupLibraryFilesInput!
  ) {
    moveGroupLibraryFiles(input: $input)
  }
`;

type IBulkMoveLibraryFilesModalProps = {
  open: boolean;
  onClose: () => void;
  rootFolderId: string;
  groupId: string;
  versionFileIds: string[];
  onAfterMove: () => void;
};

export const BulkMoveLibraryFilesModal: React.FC<
  IBulkMoveLibraryFilesModalProps
> = ({ open, onClose, rootFolderId, groupId, versionFileIds, onAfterMove }) => {
  const toast = useToast();
  const [currentFolderId, setCurrentFolderId] =
    React.useState<string>(rootFolderId);
  const { error, data, networkStatus } = useQueryWithPastDataDeprecated<
    BulkMoveLibraryFilesModal_libraryFolder,
    BulkMoveLibraryFilesModal_libraryFolderVariables
  >(GET_LIBRARY_FOLDERS, {
    variables: {
      input: {
        id: currentFolderId,
      },
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const [moveFilesFunc, { loading: loadingSubmit }] = useMutation<
    BulkMoveLibraryFilesModal_moveGroupLibraryFiles,
    BulkMoveLibraryFilesModal_moveGroupLibraryFilesVariables
  >(MOVE_LIBRARY_FILES_AST, {
    awaitRefetchQueries: true,
    refetchQueries: ['GroupResourcesPane_LibraryFolderContents'],
  });

  React.useEffect(() => {
    setCurrentFolderId(rootFolderId);
  }, [rootFolderId]);

  const onSubmitMoveHandler = async (
    destinationFolderId: string
  ): Promise<void> => {
    try {
      await moveFilesFunc({
        variables: {
          input: {
            groupId: groupId,
            versionFileIds: versionFileIds,
            toFolderId: destinationFolderId,
          },
        },
      });
      onAfterMove();
      onClose();
      toast({ status: 'success', description: 'Moved files.' });
    } catch (err) {
      toast({ status: 'error', description: 'Failed to move files.' });
    }
  };

  if (error) {
    throw new Error('error getting folders');
  }

  const currentFolder = !!data?.libraryFolderV2?.result
    ? data.libraryFolderV2.result
    : undefined;
  const subFolders = !!currentFolder ? currentFolder.subFolders.results : [];
  const loadingFolders =
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.setVariables;

  return (
    <MoveItemsModal
      subFolders={subFolders}
      open={open}
      rootFolderId={rootFolderId}
      currentFolder={currentFolder}
      loadingFolders={loadingFolders}
      loadingSubmit={loadingSubmit}
      onClickCancel={onClose}
      onClickNavigateToFolder={(folderId) => setCurrentFolderId(folderId)}
      onClickSubmitMove={onSubmitMoveHandler}
      onClose={onClose}
    />
  );
};
